import { useEffect, useState, type FormEvent, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateVN } from '@rs/contracts'
import { LanguagePicker } from '@rs/ui'
import { loadKeyGroups, tDynamic } from '@rs/ui/i18n'
import { messageOf } from '../shared/queries.ts'
import { podUrl, trackApi, type TrackView } from './public-tracking/api.ts'

/**
 * ══ TRA CỨU ĐƠN HÀNG CÔNG KHAI `/tra-cuu` (F20) ═══════════════════════════════════════════════
 *
 * ⭐ TRANG DUY NHẤT KHÁCH HÀNG ĐỌC, và trang duy nhất chạy khi không ai đăng nhập. Hai hệ quả:
 *   ① MỌI CON SỐ Ở ĐÂY PHẢI ĐÚNG. Nhãn mượn từ chỗ khác mà lệch nghĩa (VD gọi "ngày chạy" là
 *     "ngày lấy hàng") thì người đọc không có cách nào biết mình đang đọc sai;
 *   ② KHÔNG CÓ GIÁ. Máy chủ đã cắt (`TrackViewDto` là danh sách cho phép); màn cũng không đi tìm.
 *
 * ⚠ ĐÂY LÀ CHỖ ĐẶT `<LanguagePicker>` QUAN TRỌNG NHẤT (B6 · D03b §7.1). Bản cũ chỉ có nút hai
 *   chiều `vi↔en` (`PublicTracking.tsx:65`), tức khách xem 中文 VĨNH VIỄN không đổi được — họ
 *   không đăng nhập được để vào phần cài đặt. Bộ chọn đủ ba thứ tiếng phải ở ngay trên trang.
 *
 * ⚠ SAI VẾ NÀO CŨNG MỘT CÂU. Máy chủ trả cùng một câu cho "sai mã số thuế" và "sai mã đơn"; màn
 *   hiện nguyên câu đó, KHÔNG tự đoán thêm "hình như bạn gõ nhầm mã đơn".
 */

const st = (v: TrackView, i: number): 'done' | 'now' | 'todo' =>
  i === v.currentStep ? 'now' : v.currentStep >= 0 && i < v.currentStep ? 'done' : 'todo'

const TINT: Record<string, string> = {
  now: 'bg-brand-500 text-white border-brand-500',
  done: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  todo: 'bg-slate-50 text-slate-400 border-slate-200',
}

const Row = ({ label, value }: { label: string; value: string | null }): JSX.Element | null => {
  if (value === null || value.trim() === '') return null
  return (
    <div>
      <div className="text-[10px] text-slate-400 font-semibold uppercase">{label}</div>
      <div className="text-sm text-slate-700">{value}</div>
    </div>
  )
}

export const PublicTrackingPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [ready, setReady] = useState(false)
  const [tax, setTax] = useState('')
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [view, setView] = useState<TrackView | null>(null)
  /** Hai vế của lượt tra ĐÃ THÀNH CÔNG — địa chỉ ảnh POD cần chúng, và ô nhập thì sửa được. */
  const [asked, setAsked] = useState<{ tax: string; ref: string } | null>(null)

  useEffect(() => {
    let alive = true
    // `az` có ở đây vì nhãn tám trạng thái đơn (`az.ordSt.*`) dùng chung với màn nội bộ — khách và
    // điều hành phải đọc CÙNG một chữ cho cùng một bước. Thiếu nhóm là MÀN TRẮNG (cổng
    // `tools/i18n-group-loaded.test.ts`).
    void loadKeyGroups('pubTrack', 'az', 'app', 'common').then(() => { if (alive) setReady(true) })
    return () => { alive = false }
  }, [])

  if (!ready) return <p>{t('common.loading')}</p>

  const submit = (e: FormEvent): void => {
    e.preventDefault()
    // Ô trống: bấm không làm gì, nút KHÔNG mờ sẵn — đúng bản cũ `PublicTracking.tsx:69`
    if (busy || tax.trim() === '' || ref.trim() === '') return
    setBusy(true); setErr(null); setView(null)
    void trackApi(tax, ref)
      .then((v) => { setView(v); setAsked({ tax, ref }) })
      .catch((x: unknown) => { setErr(messageOf(x)) })
      .finally(() => { setBusy(false) })
  }

  const reset = (): void => { setView(null); setErr(null); setAsked(null); setTax(''); setRef('') }

  return (
    <main className="min-h-full bg-gradient-to-br from-brand-50 via-white to-sky-100 p-4">
      <div className="w-full max-w-3xl mx-auto mt-6">
        <div className="flex justify-end mb-3"><LanguagePicker tone="light" /></div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="bg-gradient-to-r from-brand-500 to-sky-500 px-6 py-7 text-center">
            {/* logo thay dòng chữ tên app — `ps:PublicTracking.tsx:92` */}
            <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
                 className="h-12 w-auto object-contain mx-auto mb-2 bg-white rounded-lg px-2 py-1" />
            <div className="text-brand-50 text-sm">{t('pubTrack.subtitle')}</div>
          </div>

          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <h1 className="text-lg font-bold text-slate-800">{t('pubTrack.title')}</h1>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">
                  {t('pubTrack.formTax')}
                </span>
                <input value={tax} aria-label={t('pubTrack.formTax')}
                       placeholder={t('pubTrack.formTaxPh')}
                       onChange={(e) => { setTax(e.target.value) }}
                       className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-slate-500 mb-1">
                  {t('pubTrack.formRef')}
                </span>
                <input value={ref} aria-label={t('pubTrack.formRef')}
                       placeholder={t('pubTrack.formRefPh')}
                       onChange={(e) => { setRef(e.target.value) }}
                       className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
              </label>
            </div>
            {/* nút gradient, chỉ khoá lúc đang tra — `ps:PublicTracking.tsx:110-111` */}
            <button type="submit" disabled={busy}
                    className="w-full rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 text-white font-semibold py-2.5 text-sm hover:from-brand-600 hover:to-brand-700 transition disabled:opacity-50">
              {busy ? t('pubTrack.submitting') : t('pubTrack.submit')}
            </button>
          </form>

          {err !== null && (
            <div className="px-6 pb-6">
              <div role="alert"
                   className="text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                {err}
              </div>
            </div>
          )}

          {view !== null && (
            <div className="px-6 pb-6 space-y-5 border-t border-slate-100 pt-5">
              {view.cancelled ? (
                <div role="status"
                     className="text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
                  ⛔ {t('pubTrack.cancelledBanner')}
                </div>
              ) : (
                <div>
                  <h2 className="text-sm font-bold text-slate-700 mb-2">{t('pubTrack.journeyTitle')}</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {view.journey.map((s, i) => (
                      <span key={s}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${TINT[st(view, i)] ?? ''}`}>
                        {tDynamic(t, `az.ordSt.${s}`)}
                      </span>
                    ))}
                  </div>
                  {view.legTotal > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1.5">
                      {t('pubTrack.legProgress')}: {view.legDone}/{view.legTotal}
                    </div>
                  )}
                </div>
              )}

              <div>
                <h2 className="text-sm font-bold text-slate-700 mb-2">
                  {t('pubTrack.orderInfo')} — <span className="font-mono">{view.ref}</span>
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Row label={t('pubTrack.pickupAddr')} value={view.pickupAddr} />
                  <Row label={t('pubTrack.dropAddr')} value={view.dropAddr} />
                  <Row label={t('pubTrack.planDate')} value={view.planDate === null || view.planDate === '' ? '' : formatDateVN(view.planDate)} />
                  <Row label={t('pubTrack.actualDeliver')} value={view.actualDeliveredAt === null || view.actualDeliveredAt === '' ? '' : formatDateVN(view.actualDeliveredAt)} />
                  <Row label={t('pubTrack.currentLocation')} value={view.location} />
                  <Row label={t('pubTrack.trainNo')} value={view.trainNo} />
                  <Row label={t('pubTrack.cargo')} value={view.cargo} />
                  <Row label={t('pubTrack.contNo')} value={view.contNo} />
                  <Row label={t('pubTrack.contSize')} value={view.contSize} />
                  <Row label={t('pubTrack.contType')} value={view.contType} />
                </div>
              </div>

              {view.pods.length > 0 && asked !== null && (
                <div>
                  <h2 className="text-sm font-bold text-slate-700 mb-2">{t('pubTrack.podTitle')}</h2>
                  <div className="flex flex-wrap gap-2">
                    {view.pods.map((p) => (
                      <img key={p.id} src={podUrl(p.id, asked.tax, asked.ref)} alt={p.fileName}
                           className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
                    ))}
                  </div>
                </div>
              )}

              <button onClick={reset} className="text-xs font-semibold text-sky-600 hover:underline">
                {t('pubTrack.resetSearch')}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
